import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPlus, FaStickyNote, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import { STICKERS_CHANGED_EVENT, fileToStickerBlob, notifyStickersChanged, uploadSticker } from './stickers';

function StickersPanel({ onSend, canManageCompany, disabled }) {
  const rootRef = useRef(null);
  const fileInputRef = useRef(null);
  const urlsRef = useRef({});
  const [open, setOpen] = useState(false);
  const [stickers, setStickers] = useState([]);
  const [urls, setUrls] = useState({});
  const [scope, setScope] = useState('pessoal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

  const load = useCallback(async () => {
    try {
      const response = await api.get('/figurinhas');
      setStickers(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setStickers([]);
      setError(errorMessage(err, 'Figurinhas indisponíveis no momento.'));
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    window.addEventListener(STICKERS_CHANGED_EVENT, load);
    return () => window.removeEventListener(STICKERS_CHANGED_EVENT, load);
  }, [load]);

  // Miniaturas vem da API autenticada; cada arquivo e baixado uma vez por sessao.
  useEffect(() => {
    let cancelled = false;
    stickers.forEach((sticker) => {
      if (urlsRef.current[sticker.id]) return;
      urlsRef.current[sticker.id] = 'loading';
      api.get(`/figurinhas/${sticker.id}/arquivo`, { responseType: 'blob' })
        .then((response) => {
          if (cancelled) return;
          const url = URL.createObjectURL(response.data);
          urlsRef.current[sticker.id] = url;
          setUrls((current) => ({ ...current, [sticker.id]: url }));
        })
        .catch(() => { delete urlsRef.current[sticker.id]; });
    });
    return () => { cancelled = true; };
  }, [stickers]);

  useEffect(() => () => {
    Object.values(urlsRef.current).forEach((url) => {
      if (url && url !== 'loading') URL.revokeObjectURL(url);
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const blob = await fileToStickerBlob(file);
      await uploadSticker(blob, { escopo: scope, nome: file.name.replace(/\.[^.]+$/, '') });
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível adicionar a figurinha.'));
    } finally {
      setBusy(false);
    }
  };

  const send = async (sticker) => {
    setBusy(true);
    try {
      const response = await api.get(`/figurinhas/${sticker.id}/arquivo`, { responseType: 'blob' });
      await onSend(response.data);
      setOpen(false);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível enviar a figurinha.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sticker) => {
    if (!window.confirm('Remover esta figurinha da biblioteca? Mensagens já enviadas não mudam.')) return;
    setBusy(true);
    try {
      await api.delete(`/figurinhas/${sticker.id}`);
      notifyStickersChanged();
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível remover a figurinha.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="zapi-whatsapp-stickers" ref={rootRef}>
      <button
        type="button"
        className="zapi-whatsapp-emoji-button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        title="Figurinhas"
        aria-label="Figurinhas"
        aria-expanded={open}
      >
        <FaStickyNote />
      </button>
      {open && (
        <div className="zapi-whatsapp-stickers-panel" role="dialog" aria-label="Figurinhas">
          <div className="zapi-whatsapp-quick-replies-header">
            <strong>Figurinhas</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar"><FaTimes /></button>
          </div>
          {error && <div className="zapi-whatsapp-quick-replies-error">{error}</div>}
          <div className="zapi-whatsapp-quick-replies-toolbar">
            {canManageCompany && (
              <select value={scope} onChange={(event) => setScope(event.target.value)} aria-label="Adicionar para">
                <option value="pessoal">Adicionar para mim</option>
                <option value="empresa">Adicionar para a empresa</option>
              </select>
            )}
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
              <FaPlus /> {busy ? 'Aguarde...' : 'Adicionar'}
            </button>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleFile} />
          </div>
          <div className="zapi-whatsapp-stickers-grid">
            {stickers.length === 0 && !error && (
              <p className="zapi-whatsapp-quick-replies-empty">
                Nenhuma figurinha ainda. Clique em "Adicionar" ou use "Salvar figurinha" numa figurinha recebida.
              </p>
            )}
            {stickers.map((sticker) => (
              <div className="zapi-whatsapp-stickers-item" key={sticker.id}>
                <button
                  type="button"
                  className="zapi-whatsapp-stickers-send"
                  onClick={() => send(sticker)}
                  disabled={busy || !urls[sticker.id]}
                  title={`Enviar${sticker.nome ? `: ${sticker.nome}` : ''}${sticker.escopo === 'empresa' ? ' (empresa)' : ''}`}
                >
                  {urls[sticker.id] ? <img src={urls[sticker.id]} alt={sticker.nome || 'Figurinha'} /> : <span>...</span>}
                </button>
                {sticker.pode_excluir && (
                  <button type="button" className="zapi-whatsapp-stickers-remove" onClick={() => remove(sticker)} disabled={busy} aria-label="Remover figurinha">
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StickersPanel;
